import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { banners } from '@/mocks/banners';

const featured = movies.filter((movie) => movie.featured);

const slides = featured.map((movie) => ({
  movie,
  image: banners.find((banner) => banner.movieId === movie.id)?.image ?? movie.poster,
}));

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState('');

  const go = useCallback((next: number) => {
    setIndex((next + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [index]);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  }

  const current = slides[index];

  return (
    <section className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[600px]">
      {slides.map((slide, i) => (
        <div
          key={slide.movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.movie.title} 焦点图`}
            title={`${slide.movie.title} ${slide.movie.categoryName}`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/35 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-4 md:px-6">
        <div key={current.movie.id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
              精选推荐
            </span>
            <span className="rounded-md border border-background-50/30 px-2.5 py-1 text-xs text-background-50">
              {current.movie.categoryName}
            </span>
            <span className="flex items-center gap-1 font-label text-sm text-accent-400">
              <i className="ri-star-fill" />
              {current.movie.score.toFixed(1)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {current.movie.title}
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
            {current.movie.summary}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-primary-200">
            <span>{current.movie.year}</span>
            <span className="text-primary-400">·</span>
            <span>{current.movie.region}</span>
            <span className="text-primary-400">·</span>
            <span>导演 {current.movie.director}</span>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.movie.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line" />
              查看详情
            </Link>
            <Link
              to={`/category/${current.movie.categoryId}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-apps-2-line" />
              浏览{current.movie.categoryName}
            </Link>
          </div>

          <form
            onSubmit={submitSearch}
            className="mt-7 flex w-full max-w-lg items-center gap-2 rounded-full bg-background-50/95 px-4 py-2.5"
          >
            <i className="ri-search-line text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品、类型或资讯关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-primary-700 px-4 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-800"
            >
              搜索
            </button>
          </form>
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.movie.id}
                type="button"
                aria-label={`切换到 ${slide.movie.title}`}
                onClick={() => go(i)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/40 hover:bg-background-50/70'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(index - 1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(index + 1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}